/* eslint-disable prettier/prettier */
/* eslint-disable import/order */
import { FC } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button, Stack, TextField } from '@mui/material';
import { Form } from '@bvi/common-components';
import { useUpdateDamageMutation, useGetByIdDamageQuery } from '@bvi/admin-panel/entities/damage/api-slice';

interface IEditDamageFormProperties {
  id: number;
  onSuccess: () => void;
}

interface IEditDamageFormValues {
  name: string;
}

export const EditDamageForm: FC<IEditDamageFormProperties> = ({
  id,
  onSuccess,
}) => {
  const { t } = useTranslation('translation');
  const [updateDamage, { isLoading: isUpdatingDamage }] = useUpdateDamageMutation();
  const { data: damageData } = useGetByIdDamageQuery(id);

  const damage = damageData?.success ? damageData.payload.data : null;

  const validationSchema = z.object({
    name: z.string().min(1, t('manageList.form.fields.name.label') as string),
  });

  const methods = useForm<IEditDamageFormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: damage?.name ?? '',
    },
    values: damage ? {
      name: damage.name,
    } : undefined,
  });

  const onSubmit = async (data: IEditDamageFormValues) => {
    try {
      await updateDamage([id, { name: data.name }]).unwrap();
      onSuccess();
    } catch {
      // Error handling - mutation will show error state
    }
  };

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <TextField
            {...methods.register('name')}
            label={t('manageList.form.fields.name.label')}
            placeholder={t('manageList.form.fields.name.placeholder')}
            error={Boolean(methods.formState.errors.name)}
            helperText={methods.formState.errors.name?.message}
            fullWidth
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onSuccess}
              disabled={isUpdatingDamage}
            >
              {t('manageList.form.actions.cancel.caption')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdatingDamage}
            >
              {t('manageList.form.actions.save.caption')}
            </Button>
          </Stack>
        </Stack>
      </Form>
    </FormProvider>
  );
};

